import React, {
  Fragment,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import cn from 'classnames'
import ExternalLink from '@groovehq/internal-design-system/lib/assets/icons/ExternalLink'
import EmptyMessage from 'components/App/DesktopView/Layout/RightSidebar/InAppCards/shared/EmptyMessage'
import CollapsableContent from 'components/CollapsableContent'
import { KeyValue } from 'components/KeyValues'
import { Tab } from 'util/ui'
import { timeInHuman, strftime } from 'util/date'
import SanitizedHTML from 'shared/components/ui/SanitizedHTML'
import NavigatableIntegrationWidgetCard from 'components/integrations/NavigatableIntegrationWidgetCard'
import {
  CUSTOMER_MARKETING_OPT_IN_LEVEL,
  ORDER_DISPLAY_FINANCIAL_STATUS_TEXT,
} from 'ducks/integrations/shopify/constants'
import { moneyString } from 'util/currency'

import {
  selectEventsForCustomerIdGroupedByDay,
  selectOrdersForCustomerId,
} from 'ducks/integrations/shopify'

import doFetchCustomerId from 'ducks/integrations/shopify/operations/doFetchCustomerId'
import { styles } from './styles'

import CreateOrderButton from './CreateOrderButton'

const NoCustomer = () => {
  return (
    <div className="grui p-7">
      <EmptyMessage>There is no customer information</EmptyMessage>
    </div>
  )
}

const RemainingFields = ({
  addresses,
  currency,
  customerId,
  hasNote,
  hasTimelineComment,
  locale,
  lastOrderId,
  marketingOptInLevel,
  productSubscriberStatus,
  state,
  taxExempt,
  taxExemptions,
}) => {
  return (
    <Fragment>
      <KeyValue title="Addresses" value={addresses} />
      <KeyValue title="Currency" value={currency} />
      <KeyValue title="Shopify customer ID" value={customerId} />
      <KeyValue title="Has note" value={hasNote} />
      <KeyValue title="Has timeline comment" value={hasTimelineComment} />
      <KeyValue title="Last order ID" value={lastOrderId} />
      <KeyValue title="Locale" value={locale} />
      <KeyValue
        title="Marketing opt-in level"
        value={
          marketingOptInLevel &&
          CUSTOMER_MARKETING_OPT_IN_LEVEL[marketingOptInLevel]
        }
      />
      <KeyValue
        title="Product subscriber status"
        value={productSubscriberStatus}
      />
      <KeyValue title="state" value={state} />
      <KeyValue title="tax exemptions" value={taxExemptions} />
      <KeyValue title="tax exempt" value={taxExempt} />
    </Fragment>
  )
}

const TimelineEvent = ({ timelineEvent }) => {
  const {
    message,
    createdAt,
    attributeToApp,
    appTitle,
    __typename,
  } = timelineEvent
  const isComment = __typename === 'CommentEvent'
  const time = useMemo(
    () => {
      return strftime('%H:%M %p', createdAt)
    },
    [createdAt]
  )
  const author = attributeToApp ? appTitle : ''

  return (
    <div
      className={cn('shopifyV2-events-event', {
        'shopifyV2-events-comment': isComment,
      })}
    >
      <div className="shopifyV2-events-event-message">
        {isComment && (
          <div className="shopifyV2-events-event-author shopifyV2-textClip">
            {author}
          </div>
        )}
        <SanitizedHTML html={message} className="shopifyV2-textClip" />
      </div>
      <div className="shopifyV2-events-event-time">{time}</div>
    </div>
  )
}

const TimelineEventsForDay = ({ day, timelineEvents }) => {
  const dateString = useMemo(
    () => {
      return timeInHuman(new Date(day), {
        showToday: true,
        hideTodayTime: true,
      })
    },
    [day]
  )

  if (!timelineEvents) return <Fragment />
  return (
    <div className="shopifyV2-events-dayGroup">
      <div className="shopifyV2-events-dayLabel">{dateString}</div>
      {timelineEvents.map(timelineEvent => (
        <TimelineEvent timelineEvent={timelineEvent} key={timelineEvent.id} />
      ))}
    </div>
  )
}

const TimelineEvents = ({ timelineEventsByDay }) => {
  if (!timelineEventsByDay) return <Fragment />

  const eventGroups = []
  timelineEventsByDay.forEach((events, day) => {
    eventGroups.push(
      <TimelineEventsForDay
        day={day}
        key={events[0]?.id}
        timelineEvents={events}
      />
    )
  })

  return (
    <div className="grui p-7 shopifyV2-events">
      {eventGroups.slice(0, 4).map(eventGroup => eventGroup)}
      {eventGroups.length > 5 && (
        <CollapsableContent
          hideTrigger="– Hide events"
          showTrigger="+ Show more events"
        >
          <div className="shopifyV2-events-more">
            {eventGroups.slice(4).map(eventGroup => eventGroup)}
          </div>
        </CollapsableContent>
      )}
    </div>
  )
}

const Order = ({ order, doNavigatableSetState, integration }) => {
  const onClick = useCallback(
    () => {
      doNavigatableSetState({ orderId: order.id, integration })
    },
    [doNavigatableSetState, order, integration]
  )

  return (
    <NavigatableIntegrationWidgetCard.ListItem
      onClick={onClick}
      subTitle={
        <div className="shopifyV2-order-status">
          {order.date} ·{' '}
          <span
            className={`shopifyV2-order-status-${order.displayFinancialStatus}`}
          >
            {ORDER_DISPLAY_FINANCIAL_STATUS_TEXT[order.displayFinancialStatus]}
          </span>
        </div>
      }
      title={
        order.isCancelled ? (
          <span className="shopifyV2-order-cancelled">{order.name}</span>
        ) : (
          order.name
        )
      }
    />
  )
}

const CustomerDetails = props => {
  const {
    totalSpent,
    ordersCount,
    customerGid,
    lifetimeDuration,
    averageOrderAmount,
    tags,
    note,
    integration,
    currency,
    orders,
    fetchMoreOrders,
    isFetching,
    isFetched,
  } = props

  const customerId = customerGid.split('/').pop()
  const averageOrderAmountString = moneyString({
    amount: averageOrderAmount,
    currencyCode: currency,
  })
  const customerUrl = `https://${
    integration.storeDomain
  }.myshopify.com/admin/customers/${customerId}`

  const totalSpentString = moneyString({
    amount: totalSpent,
    currencyCode: currency,
  })

  return (
    <Fragment>
      <div className="grui p-7">
        <KeyValue title="Lifetime duration" value={lifetimeDuration} />
        <KeyValue title="Total spent" value={totalSpentString} />
        <KeyValue title="Orders count" value={ordersCount} />
        <KeyValue
          title="Average order amount"
          value={averageOrderAmountString}
        />
        <KeyValue title="Tags" value={tags} />
        <KeyValue title="Note" value={note} />

        <CollapsableContent
          hideTrigger="– Hide fields"
          showTrigger="+ Show all fields"
        >
          <div className="shopifyV2-more">
            <RemainingFields {...props} />
          </div>
        </CollapsableContent>
      </div>

      <div className="shopifyV2-header-label">Recent orders</div>
      {orders &&
        orders.map(order => <Order order={order} key={order.id} {...props} />)}

      {(!orders || orders.length === 0) && (
        <div className="grui p-7">No orders found</div>
      )}

      {orders &&
        orders.length > 0 &&
        !isFetched && (
          <div css={styles.showMore}>
            {isFetching && 'Fetching...'}
            {!isFetching && (
              <a href="" onClick={fetchMoreOrders} rel="noopener noreferrer">
                + Show more orders
              </a>
            )}
          </div>
        )}

      <div className="shopifyV2-footer">
        <CreateOrderButton {...props} />
        <a
          href={customerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="grui ml-auto"
        >
          View in Shopify<ExternalLink className="shopifyV2-externalLink-icon" />
        </a>
      </div>
    </Fragment>
  )
}

const EVENTS_LIMIT = 15
const INITIAL_ORDERS_LIMIT = 3
const MORE_ORDERS_LIMIT = 10

const DetailsContainer = props => {
  const {
    integration,
    handle,
    fetchEvents,
    fetchOrders,
    fetchShop,
    customerGid,
  } = props

  const [isFetching, setFetching] = useState(false)
  const [isFetched, setFetched] = useState(false)

  useEffect(
    () => {
      fetchShop(integration.id)
    },
    [integration.id, fetchShop]
  )

  useEffect(
    () => {
      fetchEvents(integration.id, customerGid, EVENTS_LIMIT)
      fetchOrders(integration.id, customerGid, INITIAL_ORDERS_LIMIT)
    },
    [integration.id, handle, fetchEvents, fetchOrders, customerGid]
  )

  const fetchMoreOrders = useCallback(
    async e => {
      e.preventDefault()
      const gid = `gid://shopify/Customer/${handle}`

      setFetching(true)
      await fetchOrders(integration.id, gid, MORE_ORDERS_LIMIT)
      setFetching(false)
      setFetched(true)
    },
    [fetchOrders, handle, integration]
  )

  const panes = [
    {
      menuItem: 'Customer',
      render: () => (
        <Tab.Pane as="div">
          <CustomerDetails
            {...props}
            fetchMoreOrders={fetchMoreOrders}
            isFetched={isFetched}
            isFetching={isFetching}
          />
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'Timeline',
      render: () => (
        <Tab.Pane as="div">
          <TimelineEvents {...props} />
        </Tab.Pane>
      ),
    },
  ]

  return <Tab panes={panes} secondary pointing />
}

export default function ShopifyIndex(props) {
  const { contact, customerGid, integration } = props
  const [searched, setSearched] = useState(false)
  const [searching, setSearching] = useState(false)
  const [foundCustomerGid, setFoundCustomerGid] = useState(null)
  const dispatch = useDispatch()

  const onMount = useCallback(
    () => {
      if (!contact) return
      if (!contact.email) return
      if (searched) return
      if (searching) return
      setSearching(true)
      dispatch(
        doFetchCustomerId(integration.id, contact.id, contact.email)
      ).then(fetchedCustomerId => {
        setSearched(true)
        setFoundCustomerGid(fetchedCustomerId)
        setSearching(false)
      })
    },
    [dispatch, integration, contact, searching, searched]
  )

  useEffect(onMount)

  const realCustomerGid = foundCustomerGid || customerGid

  const orders = useSelector(state =>
    selectOrdersForCustomerId(state, { customerId: realCustomerGid })
  )

  const timelineEventsByDay = useSelector(state =>
    selectEventsForCustomerIdGroupedByDay(state, {
      customerId: realCustomerGid,
    })
  )

  return !realCustomerGid ? (
    <div>
      <NoCustomer />
    </div>
  ) : (
    <DetailsContainer
      {...props}
      customerGid={realCustomerGid}
      orders={orders}
      timelineEventsByDay={timelineEventsByDay}
    />
  )
}
